/**
 * localStorage adapter for pinned products (PSS-2655).
 *
 * This module deliberately contains no React and no DOM access beyond
 * localStorage reads/writes. It is the seam a future server-backed
 * implementation replaces: swap out fetchPinnedProducts and
 * savePinnedProducts for real fetch/save calls and every UI call site
 * stays unchanged.
 */

const LEGACY_WORKFORCE_PLANNING_PIN = '/workforce-planning';
const PERSONNEL_BUDGETING_PIN = '/personnel-budgeting';

const storageKey = (entityId: number, userId: string) =>
  `psp-pinned-products-${userId}-${entityId}`;

function migrateWorkforcePlanningPin(paths: readonly string[]): string[] {
  const seen = new Set<string>();
  const migrated: string[] = [];
  for (const path of paths) {
    const next = path === LEGACY_WORKFORCE_PLANNING_PIN ? PERSONNEL_BUDGETING_PIN : path;
    if (seen.has(next)) continue;
    seen.add(next);
    migrated.push(next);
  }
  return migrated;
}

/**
 * Safely deserializes the stored pinned-products string. Falls back to `[]`
 * on missing or corrupt data, and filters out non-string entries so one bad
 * record cannot discard the rest of a user's list.
 */
export function parsePinnedProducts(raw: string | null | undefined): string[] {
  if (raw == null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return migrateWorkforcePlanningPin(
    parsed.filter((item): item is string => typeof item === 'string')
  );
}

// ─── Replace these two functions with real fetch/save when a backend
//     endpoint is available. The hook and all UI call sites are unchanged. ───

export async function fetchPinnedProducts(entityId: number, userId: string): Promise<string[]> {
  return parsePinnedProducts(localStorage.getItem(storageKey(entityId, userId)));
}

export async function savePinnedProducts(
  entityId: number,
  userId: string,
  basePaths: string[]
): Promise<void> {
  localStorage.setItem(storageKey(entityId, userId), JSON.stringify(basePaths));
}
